import { useEffect, useMemo, useState } from 'react';
import { useProductionCostsQuery } from 'src/api/queries';
import { useFilterContext } from 'src/contexts/FilterContext';
import { toApiDates } from 'src/utils/date';
import { filterProductionCostData } from 'src/utils/filters';
import { INIT_MM_DATA_STATE, MMDataState } from '../../types';
import { aggregateApiDataToDataState } from '../../utils/aggregate';
import { buildCampaignIdToGroupIds } from '../../utils/buildCampaignIdToGroupIds';

// Supposed to only be called from context
export const useFetchProductionCosts = (enabled: boolean) => {
  const {
    currentModelVersion,
    timeRange,
    campaigns,
    campaignGroups,
    filteredCampaignIds,
  } = useFilterContext();

  const campaignIdToGroupIds = useMemo(
    () => buildCampaignIdToGroupIds(campaigns, campaignGroups),
    [campaigns, campaignGroups],
  );

  const [productionCosts, setProductionCosts] =
    useState<MMDataState>(INIT_MM_DATA_STATE);

  const {
    data: apiData,
    isLoading,
    isError,
  } = useProductionCostsQuery(
    {
      customer: currentModelVersion?.customerName ?? 'missing_customer',
      model_build: currentModelVersion?.buildID ?? 'missing_buildid',
      ...toApiDates(timeRange),
    },
    enabled,
  );

  useEffect(() => {
    if (!enabled) {
      setProductionCosts(INIT_MM_DATA_STATE);
      return;
    }

    const data = apiData ?? [];

    const filteredData = filterProductionCostData(data, filteredCampaignIds);

    const newState = aggregateApiDataToDataState(
      'productionCosts',
      'productionCost',
      filteredData,
      isLoading,
      isError,
      campaignIdToGroupIds,
    );

    setProductionCosts(newState);
  }, [
    apiData,
    isLoading,
    isError,
    enabled,
    campaignIdToGroupIds,
    filteredCampaignIds,
  ]);

  return { productionCosts };
};
